import { Outlet, ScrollRestoration, useLocation } from 'react-router'
import { Chrome } from '@/components/chrome/Chrome'
import { SiteChrome } from '@/components/chrome/SiteChrome'
import { Analytics } from '@/components/analytics/Analytics'
import { PROMO_VARIANT } from '@/pages'
import { PAGE_META } from '@/content/pages/pages.generated'

/** Internal tooling routes render bare, without the site chrome. */
const BARE_ROUTES = ['/styleguide', '/content-preview']

/**
 * `/spec-preview/<slug>` stands in for the route that slug belongs to.
 *
 * Only so the promo strip matches: the preview exists to be diffed against the
 * ported page, and a missing promo bar offsets everything below it by its own
 * height, which reads as a whole-page failure. Resolved from `PAGE_META` rather
 * than a second mapping.
 */
function previewRouteFor(pathname: string): string | null {
  const slug = pathname.match(/^\/spec-preview\/([^/]+)$/)?.[1]
  if (!slug) return null
  return PAGE_META.find((m) => m.slug === slug)?.route ?? null
}

export function RootLayout() {
  const { pathname } = useLocation()
  const route = previewRouteFor(pathname) ?? (pathname === '/' ? '/' : pathname.replace(/\/$/, ''))
  if (BARE_ROUTES.includes(pathname)) {
    return (
      <>
        <ScrollRestoration />
        <Outlet />
      </>
    )
  }
  /*
   * The header, promo strip and footer wrap every production route — the 17
   * still rendering ported markup and the 42 the design system renders — so
   * they belong here rather than in either kind of page. `Chrome` is only the
   * Webflow runtime loader; `SiteChrome` is the markup.
   */
  return (
    <>
      <ScrollRestoration />
      <Chrome>
        <SiteChrome promo={PROMO_VARIANT[route] ?? null}>
          <Outlet />
        </SiteChrome>
      </Chrome>
      <Analytics />
    </>
  )
}
